/**
 * 多账号本地登记表（「切换账号」的数据底座）
 *
 * 与移动端 utils/auth.js 的 accounts 模型保持一致，同一套字段、同一个上限，
 * 这样两端对「已保存的账号」的理解不会分叉。
 *
 * 存储分工（web 与移动端的差异都收敛在这里，其它地方不用关心）：
 * - token / refreshToken 在 cookie（cookieUtil），user 在 sessionStorage —— 三者合起来
 *   表示「当前正在使用」的那个账号，全站已有代码都按这个约定读写，不做改动；
 * - accounts 放 localStorage —— 它必须活得比一次会话久，否则关掉标签页再回来
 *   账号列表就空了，切换账号功能等于不存在。
 *
 * 注意 sessionStorage 是按标签页隔离的：新标签页打开站点时 accounts 读得到，
 * 但「当前账号」是该标签页自己的登录态。这是浏览器的既有语义，不去对抗它。
 */
import {
  getToken,
  getRefreshToken,
  setToken,
  setRefreshToken,
  removeRefreshToken
} from '@/utils/cookieUtil'

// localStorage 中的账号列表 key，与移动端同名，便于两端对照排查
const ACCOUNTS_KEY = 'accounts'

// 最多保存的账号数量，与移动端一致
export const MAX_ACCOUNTS = 8

/**
 * 读取当前登录用户（sessionStorage 里的 user 快照）
 *
 * 不从 vuex 读：本模块会被 Header / Login 等在 store 初始化前后的各种时机调用，
 * 直接读存储更稳，也避免循环依赖。
 *
 * @returns {Object|null}
 */
function readCurrentUser () {
  try {
    const raw = sessionStorage.getItem('user')
    if (!raw) return null
    const user = JSON.parse(raw)
    return user && user.id ? user : null
  } catch (e) {
    return null
  }
}

/**
 * 获取已保存的账号列表（最近使用的在前）
 *
 * @returns {Array} 账号数组，异常或未初始化时为空数组
 */
export function getAccounts () {
  try {
    const raw = localStorage.getItem(ACCOUNTS_KEY)
    if (!raw) return []
    const list = JSON.parse(raw)
    // 只保留有 id 且有 token 的条目：缺 token 的条目切过去必然 401，
    // 留在列表里只会让用户点一次才知道不能用
    return Array.isArray(list) ? list.filter(item => item && item.id && item.token) : []
  } catch (e) {
    console.error('读取账号列表失败:', e)
    return []
  }
}

/**
 * 写回账号列表
 *
 * @param {Array} list
 * @returns {Array} 写入的列表
 */
function saveAccounts (list) {
  try {
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(list))
  } catch (e) {
    // localStorage 写满或被隐私模式禁用时不应该影响主流程
    console.error('保存账号列表失败:', e)
  }
  return list
}

/**
 * 把用户数据整理成账号条目
 *
 * 以旧快照为基础做增量合并：局部更新（只改头像、只刷新 token）不能把
 * 其它字段抹掉，否则切回该账号时展示的是一份残缺资料。
 *
 * @param {Object} userData 用户数据，可以是完整资料也可以是局部字段
 * @param {Object} [existing] 列表中已存在的同 id 旧条目
 * @returns {Object} 账号条目
 */
function buildAccountEntry (userData, existing) {
  const prevInfo = (existing && existing.userInfo) || {}
  const mergedInfo = { ...prevInfo, ...userData }

  // token 允许沿用旧值：纯资料同步（比如绑定手机号后回写）通常不带 token
  const token = userData.token || (existing && existing.token) || mergedInfo.token || ''
  const refreshToken =
    userData.refreshToken || (existing && existing.refreshToken) || mergedInfo.refreshToken || ''
  mergedInfo.token = token
  mergedInfo.refreshToken = refreshToken

  return {
    id: mergedInfo.id,
    token,
    refreshToken,
    nickname: mergedInfo.nickname || '',
    avatar: mergedInfo.avatar || '',
    phone: mergedInfo.phone || '',
    loginAccount: mergedInfo.loginAccount || '',
    school: mergedInfo.school || '',
    // 顺序权重：登录/切换时刷新，纯资料同步时沿用旧值
    loginTime: (existing && existing.loginTime) || Date.now(),
    // 保留完整资料，切换时可直接恢复到 sessionStorage 与 vuex
    userInfo: mergedInfo
  }
}

/**
 * 新增或更新一个账号（按 id 去重）
 *
 * @param {Object} userData 用户数据，必须含 id；含 token 时才可能新建条目
 * @param {Object} [options]
 * @param {Boolean} [options.moveToFront=true] 是否置顶为「最近使用」。
 *        登录 / 切换传 true；只是同步资料时传 false，避免列表顺序无故跳动。
 * @returns {Array} 更新后的账号列表
 */
export function upsertAccount (userData, options = {}) {
  if (!userData || !userData.id) {
    return getAccounts()
  }

  const { moveToFront = true } = options
  const list = getAccounts()
  const prevIndex = list.findIndex(item => String(item.id) === String(userData.id))
  const existing = prevIndex >= 0 ? list[prevIndex] : null
  const entry = buildAccountEntry(userData, existing)

  // 没有任何可用 token 就不写入，避免脏条目导致切换失败
  if (!entry.token) {
    return list
  }

  let next = list.filter(item => String(item.id) !== String(userData.id))

  if (moveToFront || !existing) {
    entry.loginTime = Date.now()
    next.unshift(entry)
  } else {
    next.splice(prevIndex, 0, entry)
  }

  if (next.length > MAX_ACCOUNTS) {
    next = next.slice(0, MAX_ACCOUNTS)
  }

  return saveAccounts(next)
}

/**
 * 把「当前正在使用」的账号回写进列表快照
 *
 * 任何改动了当前账号资料或凭证的地方调用一次，切换账号时用本地快照秒切才不会串号。
 * 典型调用点：登录成功、绑定手机号成功、打开切换账号面板、切换前。
 *
 * @returns {Array} 更新后的账号列表
 */
export function syncCurrentAccount () {
  const user = readCurrentUser()
  const token = getToken()
  if (!user || !token) {
    return getAccounts()
  }
  return upsertAccount(
    { ...user, token, refreshToken: getRefreshToken() },
    { moveToFront: false }
  )
}

/**
 * 切换到指定账号：把该账号的凭证提升为「当前使用」
 *
 * 纯本地操作，不发请求 —— 每个条目都存着自己那份仍然有效的 token，
 * 这也是「切换无需重新登录」的由来。调用方拿到返回值后需要整页刷新，
 * 让各页面以新身份重新取数。
 *
 * @param {String|Number} id 目标账号 id
 * @returns {Object|null} 切换后的用户资料，目标不存在或无 token 时返回 null
 */
export function switchAccount (id) {
  const target = getAccounts().find(item => String(item.id) === String(id))
  if (!target || !target.token) {
    return null
  }

  const userData = target.userInfo || target

  setToken(target.token)
  if (target.refreshToken) {
    setRefreshToken(target.refreshToken)
  } else {
    // 目标账号没有 refreshToken 时必须删掉上一个账号的，
    // 否则 401 续期会拿着别人的 refreshToken 换回别人的登录态
    removeRefreshToken()
  }
  sessionStorage.setItem('user', JSON.stringify(userData))

  // 刷新最近使用时间并置顶
  upsertAccount(userData)

  return userData
}

/**
 * 从列表中移除指定账号
 *
 * @param {String|Number} id 目标账号 id
 * @returns {Array} 更新后的账号列表
 */
export function removeAccount (id) {
  if (!id) return getAccounts()
  const next = getAccounts().filter(item => String(item.id) !== String(id))
  return saveAccounts(next)
}

/**
 * 当前正在使用的账号 id
 *
 * @returns {String|Number|null}
 */
export function getCurrentAccountId () {
  const user = readCurrentUser()
  return user ? user.id : null
}

/**
 * 清空 localStorage，但保留账号列表
 *
 * 退出登录与登录前的缓存清理都会 localStorage.clear()，直接清会把已保存的
 * 其它账号一起抹掉 —— 那样「切换账号」在退出一次后就永远是空列表。
 * 需要连账号列表一起清时传 keepAccounts = false。
 *
 * @param {Boolean} [keepAccounts=true] 是否保留账号列表
 */
export function clearLocalStorage (keepAccounts = true) {
  const snapshot = keepAccounts ? localStorage.getItem(ACCOUNTS_KEY) : null
  localStorage.clear()
  if (snapshot) {
    try {
      localStorage.setItem(ACCOUNTS_KEY, snapshot)
    } catch (e) {
      console.error('恢复账号列表失败:', e)
    }
  }
}
